import {
  aesDecryptMessage,
  aesEncryptMessage,
  exportAesCryptoKey,
  exportRsaCryptoKey,
  generateAesKey,
  generateRsaKey,
  importAesKey,
  importRsaKey,
  rsaDecryptMessage,
  rsaEncryptMessage,
} from "./webCrypto";
import { buildCanonicalString, buildSortedQueryStringFromUrl, hmacSha256Base64 } from "./crypto";

export interface SecureEncryptResult {
  headers: Record<string, string>;
  body?: string;
  clientKeyPair: CryptoKeyPair;
  nonce: string;
}

let cachedKeyPair: CryptoKeyPair | null = null;
let pendingKeyPair: Promise<CryptoKeyPair> | null = null;

export async function getOrCreateClientKeyPair(): Promise<CryptoKeyPair> {
  if (cachedKeyPair) return cachedKeyPair;
  if (pendingKeyPair) return pendingKeyPair;
  pendingKeyPair = generateRsaKey()
    .then((pair) => {
      cachedKeyPair = pair;
      pendingKeyPair = null;
      return pair;
    })
    .catch((err) => {
      pendingKeyPair = null;
      throw err;
    });
  return pendingKeyPair;
}

function generateIvBase64(): string {
  const iv = window.crypto.getRandomValues(new Uint8Array(12));
  let bin = "";
  iv.forEach((b) => (bin += String.fromCharCode(b)));
  return btoa(bin);
}

export async function prepareSecureRequest(params: {
  method: string;
  url: string;
  serverPublicKey?: string | null;
  hmacSecret: string | null;
  plainBody?: unknown;
}): Promise<SecureEncryptResult> {
  const hmacSecret = params.hmacSecret?.trim();
  if (!hmacSecret) throw new Error("SecureApi: hmacSecret is missing.");
  if (!params.serverPublicKey) throw new Error("SecureApi: server public key is missing.");

  const { method, url, serverPublicKey, plainBody } = params;
  const { servletPath, sortedQueryString } = buildSortedQueryStringFromUrl(url);

  const aesKey = await generateAesKey();
  const clientSymmetric = await exportAesCryptoKey(aesKey);

  let encryptedB64 = "";
  let clientIv = "";
  let body: string | undefined;

  if (plainBody !== undefined && plainBody !== null) {
    const { aesCipheredText, ivBase64 } = await aesEncryptMessage(aesKey, JSON.stringify(plainBody));
    encryptedB64 = aesCipheredText;
    clientIv = ivBase64;
    body = JSON.stringify({ encryptedBody: encryptedB64 });
  } else {
    clientIv = generateIvBase64();
  }

  const timestamp = String(Date.now());
  const nonce = crypto.randomUUID();
  const envelope = JSON.stringify({ clientSymmetric, clientIv, timestamp, nonce });
  const serverPubKey = await importRsaKey(serverPublicKey);
  const encryptedKeyB64 = await rsaEncryptMessage(serverPubKey, envelope);

  const canonical = await buildCanonicalString(
    method,
    servletPath,
    sortedQueryString,
    timestamp,
    nonce,
    encryptedB64,
  );
  const signature = await hmacSha256Base64(canonical, hmacSecret);
  const clientKeyPair = await getOrCreateClientKeyPair();
  const clientPubKeyB64 = await exportRsaCryptoKey(clientKeyPair.publicKey);

  return {
    headers: {
      "X-Client-Public-Key": clientPubKeyB64,
      "X-Encrypted-Key": encryptedKeyB64,
      "X-Timestamp": timestamp,
      "X-Nonce": nonce,
      "X-Signature": signature,
    },
    body,
    clientKeyPair,
    nonce,
  };
}

export async function decryptSecureResponse<T>(params: {
  response: Response;
  clientKeyPair: CryptoKeyPair;
  expectedNonce: string;
}): Promise<T> {
  const { response, clientKeyPair, expectedNonce } = params;
  const responseEncryptedKey = response.headers.get("X-Response-Encrypted-Key");
  const encParsedObject = await response.json();

  const rsaDecryptedText = await rsaDecryptMessage(
    clientKeyPair.privateKey,
    responseEncryptedKey ?? "",
  );
  const headerDto = JSON.parse(rsaDecryptedText) as {
    symmetricSecret: string;
    responseIV: string;
    requestId: string;
  };

  if (headerDto.requestId !== expectedNonce) {
    throw new Error(
      `SecureApi: response requestId mismatch. Expected ${expectedNonce}, got ${headerDto.requestId}`,
    );
  }

  const importedAesKey = await importAesKey(headerDto.symmetricSecret);
  const plainResponse = await aesDecryptMessage(
    importedAesKey,
    headerDto.responseIV,
    encParsedObject?.response ?? "",
  );
  return JSON.parse(plainResponse) as T;
}
